import React, { useEffect, useState } from "react";
import { Plus, Trash2, MapPinned, ChevronDown, ChevronUp, RefreshCw, AlertTriangle } from "lucide-react";
import { territoriesApi, apiErrorMessage } from "@/api/client";
import { EmptyState } from "@/components/EmptyState";
import { Sheet } from "@/components/Sheet";
import { toast } from "sonner";

const KIND_LABEL = {
  postcode_prefixes: "Postcode prefixes", named: "Named", unrestricted: "Unrestricted", bbox: "Map box", authority: "FSA authority",
};

const GAP_LABEL = { coverage_gap: "Coverage gap", no_rep_assigned: "No rep assigned", no_field_time: "No field time" };

export default function Territories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [statsById, setStatsById] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => { setLoading(true); try { const { data } = await territoriesApi.list(); setItems(data.items); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const remove = async (id) => { if (!window.confirm("Delete this territory?")) return; try { await territoriesApi.remove(id); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErrorMessage(e)); } };

  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!statsById[id]) {
      try { const { data } = await territoriesApi.stats(id); setStatsById((m) => ({ ...m, [id]: data })); }
      catch (e) { toast.error(apiErrorMessage(e)); }
    }
  };

  const recompute = async (id) => {
    setBusy(id);
    try { const { data } = await territoriesApi.recompute(id); setStatsById((m) => ({ ...m, [id]: data })); toast.success("Recomputed"); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(null); }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-5 md:py-8" data-testid="territories-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Territories</h1>
        <button data-testid="territory-add-btn" onClick={() => setOpen(true)} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white hover:bg-teal-800"><Plus className="h-4 w-4" /> New</button>
      </div>
      <p className="mb-4 text-xs text-slate-400">Coverage areas for prospect alerts and deterministic gap analysis. Expand a territory to see live stats.</p>

      {loading ? <div className="h-24 animate-pulse rounded-xl border border-slate-200 bg-white" /> :
        items.length === 0 ? <EmptyState testid="territories-empty" icon={MapPinned} title="No territories yet" description="Create coverage areas for your reps." /> :
        <div className="space-y-2" data-testid="territories-list">
          {items.map((t) => {
            const data = statsById[t.id];
            const s = data?.stats; const gaps = data?.gaps || [];
            return (
              <div key={t.id} data-testid={`territory-${t.id}`} className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between p-4">
                  <button data-testid={`territory-expand-${t.id}`} onClick={() => toggle(t.id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                    {expanded === t.id ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />}
                    <span className="min-w-0">
                      <span className="block truncate font-heading text-sm font-bold text-slate-800">{t.name}</span>
                      <span className="block text-xs text-slate-500">{KIND_LABEL[t.kind]}{t.kind === "postcode_prefixes" && t.parameters?.prefixes ? ` · ${t.parameters.prefixes.join(", ")}` : ""}</span>
                    </span>
                  </button>
                  <button data-testid={`territory-remove-${t.id}`} onClick={() => remove(t.id)} className="shrink-0 pl-3 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
                {expanded === t.id && (
                  <div className="border-t border-slate-100 p-4" data-testid={`territory-stats-${t.id}`}>
                    {!data ? <div className="h-16 animate-pulse rounded-lg bg-slate-50" /> : (
                      <>
                        {gaps.length > 0 && (
                          <div className="mb-3 space-y-1.5" data-testid={`territory-gaps-${t.id}`}>
                            {gaps.map((g) => (
                              <div key={g.code} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${g.severity === "high" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {GAP_LABEL[g.code] || g.code}: {g.message}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                          <Stat label="Prospects" value={s.total_discoverable_prospects} />
                          <Stat label="Strong fits" value={s.strong_caterctrl_fits} cls="text-emerald-600" />
                          <Stat label="FHRS 4" value={s.rating_4_count} />
                          <Stat label="FHRS 5" value={s.rating_5_count} />
                          <Stat label="Warm" value={s.warm_leads} />
                          <Stat label="Unclaimed" value={s.unclaimed_leads} cls="text-amber-600" />
                          <Stat label="Open opps" value={s.active_opportunities} />
                          <Stat label="Customers" value={s.customers} />
                          <Stat label="Visits 90d" value={s.visits_last_90d} />
                          <Stat label="New 30d" value={s.new_last_30d} />
                          <Stat label="Field days" value={s.upcoming_field_days} />
                          <Stat label="Reps" value={s.assigned_rep_count} />
                        </div>
                        <button data-testid={`territory-recompute-${t.id}`} onClick={() => recompute(t.id)} disabled={busy === t.id}
                          className="mt-3 inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                          <RefreshCw className={`h-3.5 w-3.5 ${busy === t.id ? "animate-spin" : ""}`} /> Recompute
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>}

      <TerritoryDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}

function Stat({ label, value, cls = "text-slate-800" }) {
  return (
    <div className="rounded-lg bg-slate-50 p-2 text-center">
      <p className={`text-base font-bold ${cls}`}>{value ?? 0}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}

function TerritoryDialog({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("postcode_prefixes");
  const [prefixes, setPrefixes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(""); setKind("postcode_prefixes"); setPrefixes(""); } }, [open]);

  const save = async () => {
    setSaving(true);
    let parameters = {};
    if (kind === "postcode_prefixes") parameters = { prefixes: prefixes.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) };
    if (kind === "named") parameters = { label: name };
    try { await territoriesApi.create({ name, kind, parameters, active: true }); toast.success("Territory created"); onOpenChange(false); onSaved?.(); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onClose={() => onOpenChange(false)} title="New territory" testid="territory-dialog">
      <label className="mb-1 block text-xs font-semibold text-slate-600">Name</label>
      <input data-testid="territory-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mb-3 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" placeholder="North West" />
      <label className="mb-1 block text-xs font-semibold text-slate-600">Kind</label>
      <select data-testid="territory-kind-select" value={kind} onChange={(e) => setKind(e.target.value)} className="mb-3 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm">
        <option value="postcode_prefixes">Postcode prefixes</option>
        <option value="named">Named (informational)</option>
        <option value="unrestricted">Unrestricted</option>
      </select>
      {kind === "postcode_prefixes" && (
        <>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Prefixes (comma separated)</label>
          <input data-testid="territory-prefixes-input" value={prefixes} onChange={(e) => setPrefixes(e.target.value)} className="mb-4 min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm" placeholder="B1, B2, M1" />
        </>
      )}
      <button data-testid="territory-save" onClick={save} disabled={saving || !name} className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">{saving ? "Saving…" : "Create"}</button>
    </Sheet>
  );
}
